import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { play } from "../../lib/sound";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import CustomerHeader from "../../components/CustomerHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { Trash2, Plus, Minus, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const navigate = useNavigate();
  const { items, businessId, businessName, updateQty, clear, subtotal } = useCart();
  const hasService = items.some((i) => i.kind === "service");
  const { user } = useAuth();
  const [fulfillment, setFulfillment] = useState("consegna");
  const [payment, setPayment] = useState("carta");
  const [cashEnabled, setCashEnabled] = useState(false);
  const [cashGiven, setCashGiven] = useState(null);
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState(user?.address || "");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [useCredits, setUseCredits] = useState(false);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [plusDelivery, setPlusDelivery] = useState(null);

  const deliveryFee = fulfillment === "ritiro" ? 0 : (quote?.in_service_area ? quote.delivery_fee : 0);
  const effDelivery = (plusDelivery?.is_plus && plusDelivery?.applies) ? plusDelivery.plus_fee : deliveryFee;
  const serviceFee = hasService ? 0 : subtotal * 0.03;
  const preCreditTotal = Math.max(0, subtotal - discount + effDelivery + serviceFee);
  const creditsAvail = user?.credits || 0;
  const creditDiscount = useCredits ? Math.round(Math.min(creditsAvail / 100, preCreditTotal) * 100) / 100 : 0;
  const creditsUsed = Math.round(creditDiscount * 100);
  const total = Math.max(0, preCreditTotal - creditDiscount);
  const outOfArea = fulfillment === "consegna" && quote && !quote.in_service_area;
  const showCash = cashEnabled && fulfillment === "consegna";
  const isCash = payment === "contanti";
  const cashChange = isCash && cashGiven != null ? Math.max(0, Math.round((cashGiven - total) * 100) / 100) : 0;
  const cashInsufficient = isCash && cashGiven != null && cashGiven < total - 0.001;
  const cashHighChange = cashChange > 50;
  const cashValid = !isCash || (cashGiven != null && !cashInsufficient);
  const canOrder = items.length > 0 && !loading && !quoting && cashValid && (hasService || fulfillment === "ritiro" || (quote && quote.in_service_area));

  useEffect(() => {
    if (fulfillment !== "consegna" || !businessId || !address || address.trim().length < 5) { setQuote(null); setQuoting(false); return; }
    setQuoting(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.post("/delivery/quote", { business_id: businessId, address }); setQuote(data); }
      catch { setQuote(null); }
      setQuoting(false);
    }, 900);
    return () => clearTimeout(t);
  }, [address, fulfillment, businessId]);

  useEffect(() => { api.get("/payments/config").then(({ data }) => setCashEnabled(!!data.cash_enabled)).catch(() => {}); }, []);
  useEffect(() => { if (hasService && fulfillment !== "servizio") setFulfillment("servizio"); }, [hasService, fulfillment]);
  useEffect(() => { if (!showCash && payment === "contanti") { setPayment("carta"); setCashGiven(null); } }, [showCash, payment]);

  useEffect(() => {
    if (fulfillment !== "consegna" || !quote?.in_service_area || !quote.delivery_fee) { setPlusDelivery(null); return; }
    api.post("/fuma-plus/delivery-quote", { fee: quote.delivery_fee, subtotal, zone: quote.zone })
      .then(({ data }) => {
        setPlusDelivery(data);
        if (!data.is_plus && data.applies && data.saving > 0) {
          api.post("/fuma-plus/upsell-event", { event: "view", saving: data.saving, context: "checkout" }).catch(() => {});
        }
      }).catch(() => setPlusDelivery(null));
  }, [quote, subtotal, fulfillment]);

  const activatePlus = async (saving) => {
    try { await api.post("/fuma-plus/upsell-event", { event: "click", saving: saving || 0, context: "checkout" }); } catch { /* noop */ }
    try {
      const { data } = await api.post("/customer/fuma-plus/subscribe", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyCoupon = async () => {
    try {
      const { data } = await api.get(`/coupons/validate/${coupon}`);
      const d = (data.discount_percent ? subtotal * data.discount_percent / 100 : 0) + (data.discount_amount || 0);
      setDiscount(Math.min(d, subtotal));
      toast.success("Coupon applicato");
    } catch (e) { setDiscount(0); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        business_id: businessId, items, order_type: hasService ? "service" : "food", fulfillment, notes,
        coupon_code: coupon, payment_method: payment, address, credits_used: creditsUsed,
        cash_amount_given: isCash ? cashGiven : undefined,
      });
      clear();
      if (payment === "carta") {
        toast.success("Ordine creato. Reindirizzamento al pagamento…");
        const origin = window.location.origin;
        const res = await api.post("/payments/checkout", { order_id: data.id, origin_url: origin });
        localStorage.setItem("fuma_last_order", data.id);
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.success("Ordine inviato!");
      play("order_confirmed");
      navigate(`/app/orders/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setLoading(false); }
  };

  if (items.length === 0)
    return (
      <div className="min-h-screen bg-[#0A0A0C] grain">
        <CustomerHeader />
        <div className="max-w-6xl mx-auto px-4 py-24 text-center relative z-10">
          <p className="text-zinc-400 text-lg">Il carrello è vuoto.</p>
          <Button onClick={() => navigate("/app")} className="mt-6 bg-[#00E676] text-black rounded-full">Sfoglia le attività</Button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain">
      <CustomerHeader />
      <main className="max-w-4xl mx-auto px-4 lg:px-8 py-8 relative z-10">
        <h1 className="font-display font-black text-3xl tracking-tighter mb-1">Carrello</h1>
        <p className="text-zinc-400 mb-6">{businessName}</p>

        <div className="space-y-2 mb-6">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="flex items-center gap-4 bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
              <div className="flex-1"><div className="font-medium">{it.name}</div><div className="text-sm text-[#00E676]">€{it.price.toFixed(2)}</div></div>
              <div className="flex items-center gap-2">
                <button data-testid={`qty-minus-${it.product_id}`} onClick={() => updateQty(it.product_id, -1)} className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">{it.qty === 1 ? <Trash2 className="h-4 w-4 text-red-400" /> : <Minus className="h-4 w-4" />}</button>
                <span className="w-6 text-center">{it.qty}</span>
                <button data-testid={`qty-plus-${it.product_id}`} onClick={() => updateQty(it.product_id, 1)} className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            {hasService && (
              <div className="rounded-lg border border-[#7DF9FF]/30 bg-[#7DF9FF]/5 p-3 text-sm" data-testid="service-summary">
                <div className="font-semibold text-[#7DF9FF] mb-1">Prenotazione servizio</div>
                {items.filter((it) => it.kind === "service").map((it) => (
                  <div key={it.product_id} className="text-zinc-300 text-xs">{it.name}{it.slot ? ` · ${it.slot}` : ""}{it.duration_min ? ` · ${it.duration_min} min` : ""}</div>
                ))}
              </div>
            )}
            {!hasService && (<>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Modalità</Label>
              <RadioGroup value={fulfillment} onValueChange={setFulfillment} className="mt-2 space-y-2">
                <label className="flex items-center gap-3 bg-[#1C1C1F] border border-white/10 rounded-lg p-3 cursor-pointer"><RadioGroupItem value="consegna" data-testid="fulfillment-consegna" /> Consegna a domicilio</label>
                <label className="flex items-center gap-3 bg-[#1C1C1F] border border-white/10 rounded-lg p-3 cursor-pointer"><RadioGroupItem value="ritiro" data-testid="fulfillment-ritiro" /> Ritiro in negozio</label>
              </RadioGroup>
            </div>
            {fulfillment === "consegna" && (
              <div><Label>Indirizzo di consegna</Label><Input data-testid="checkout-address" value={address} onChange={(e) => setAddress(e.target.value)} className="bg-[#1C1C1F] border-white/10 mt-1.5" /></div>
            )}
            </>)}
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Pagamento</Label>
              <RadioGroup value={payment} onValueChange={setPayment} className="mt-2 space-y-2">
                <label className="flex items-center gap-3 bg-[#1C1C1F] border border-white/10 rounded-lg p-3 cursor-pointer"><RadioGroupItem value="carta" data-testid="payment-carta" /> Carta</label>
                {showCash && (
                  <label className="flex items-center gap-3 bg-[#1C1C1F] border border-white/10 rounded-lg p-3 cursor-pointer"><RadioGroupItem value="contanti" data-testid="payment-contanti" /> Contanti alla consegna</label>
                )}
              </RadioGroup>
              {isCash && (
                <div data-testid="cash-panel" className="mt-3 bg-[#0A0A0C] border border-[#00E676]/20 rounded-lg p-3 space-y-3">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Con quanto paghi in contanti?</Label>
                  <div className="flex flex-wrap gap-2">
                    {[{ label: "Esatto", val: total }, { label: "€20", val: 20 }, { label: "€50", val: 50 }, { label: "€100", val: 100 }].map((o) => {
                      const v = Math.round(o.val * 100) / 100;
                      return (
                        <button key={o.label} type="button" data-testid={`cash-opt-${o.label}`} onClick={() => setCashGiven(v)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${cashGiven === v ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>{o.label}</button>
                      );
                    })}
                  </div>
                  <Input type="number" step="0.01" min={total} data-testid="cash-custom" placeholder="Importo personalizzato €"
                    value={cashGiven ?? ""} onChange={(e) => setCashGiven(e.target.value === "" ? null : (parseFloat(e.target.value) || 0))}
                    className="bg-[#1C1C1F] border-white/10" />
                  {cashGiven != null && (cashInsufficient
                    ? <div data-testid="cash-insufficient" className="text-red-400 text-xs">L'importo è inferiore al totale (€{total.toFixed(2)}).</div>
                    : <div data-testid="cash-change" className="text-sm">Resto: <b className="text-[#00E676]">€{cashChange.toFixed(2)}</b></div>)}
                  {cashHighChange && !cashInsufficient && (
                    <div data-testid="cash-high-change" className="text-amber-400 text-xs">Resto elevato (oltre €50): il rider potrebbe non avere l'importo esatto. Se puoi, scegli una cifra più vicina al totale.</div>
                  )}
                </div>
              )}
            </div>
            <div><Label>Note</Label><Textarea data-testid="checkout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-[#1C1C1F] border-white/10 mt-1.5" placeholder="Citofono, allergie…" /></div>
          </div>

          <div>
            <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 sticky top-24">
              <div className="flex gap-2 mb-4">
                <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Codice coupon" className="bg-[#0A0A0C] border-white/10" />
                <Button data-testid="apply-coupon" onClick={applyCoupon} variant="secondary">Applica</Button>
              </div>
              {creditsAvail > 0 && (
                <label data-testid="use-credits-toggle" className="flex items-center justify-between gap-2 bg-[#0A0A0C] border border-[#00E676]/30 rounded-lg p-3 mb-4 cursor-pointer">
                  <span className="text-sm">Usa i miei crediti <b className="text-[#00E676]">({creditsAvail} = €{(creditsAvail / 100).toFixed(2)})</b></span>
                  <input type="checkbox" checked={useCredits} onChange={(e) => setUseCredits(e.target.checked)} className="h-5 w-5 accent-[#00E676]" />
                </label>
              )}
              <div className="space-y-2 text-sm border-t border-white/10 pt-4">
                <Row label="Subtotale" value={subtotal} />
                {discount > 0 && <Row label="Sconto" value={-discount} green />}
                {creditDiscount > 0 && <Row testid="credit-row" label={`Crediti (${creditsUsed} usati)`} value={-creditDiscount} green />}
                {fulfillment === "consegna" ? (
                  quoting ? <div data-testid="quote-loading" className="text-zinc-400 text-xs">Calcolo consegna in corso…</div> :
                  outOfArea ? <div data-testid="out-of-area" className="text-red-400 text-xs py-1">{quote.reason}</div> :
                  quote && quote.in_service_area ? (
                    <div className="flex justify-between items-center" data-testid="delivery-row">
                      <span className="text-zinc-400">Consegna · {quote.distance_km} km · ~{quote.duration_min} min</span>
                      {plusDelivery?.is_plus && plusDelivery?.applies && plusDelivery.saving > 0
                        ? <span className="flex items-center gap-2"><span className="text-zinc-500 line-through text-xs">€{deliveryFee.toFixed(2)}</span><span className="text-[#FFD700] font-semibold">{effDelivery === 0 ? "Gratis" : `€${effDelivery.toFixed(2)}`}</span></span>
                        : <span>€{deliveryFee.toFixed(2)}</span>}
                    </div>
                  ) :
                  <div className="text-zinc-500 text-xs py-1">Inserisci l'indirizzo per calcolare la consegna</div>
                ) : null}
                {fulfillment === "consegna" && !plusDelivery?.is_plus && plusDelivery?.applies && plusDelivery.saving > 0 && (
                  <div data-testid="checkout-plus-upsell" className="rounded-lg border border-[#FFD700]/40 bg-[#FFD700]/8 p-3 my-1">
                    <div className="flex items-start gap-2 text-sm">
                      <Crown className="h-4 w-4 text-[#FFD700] shrink-0 mt-0.5" />
                      <span>Con <b>Fuma+</b> questa consegna {plusDelivery.plus_fee === 0 ? "sarebbe gratuita" : `costerebbe €${plusDelivery.plus_fee.toFixed(2)}`}. Risparmieresti <b className="text-[#FFD700]">€{plusDelivery.saving.toFixed(2)}</b>.</span>
                    </div>
                    {plusDelivery.recovers_subscription && <div className="text-xs text-zinc-400 mt-1">Con questa sola consegna recuperi €{plusDelivery.saving.toFixed(2)} dei €{Number(plusDelivery.price_month).toFixed(2)} di Fuma+.</div>}
                    {plusDelivery.month_saved > 0 && <div className="text-xs text-zinc-400 mt-0.5">Questo mese avresti già risparmiato €{plusDelivery.month_saved.toFixed(2)} con Fuma+.</div>}
                    <Button data-testid="checkout-activate-plus" onClick={() => activatePlus(plusDelivery.saving)} className="mt-2 h-8 bg-[#00E676] text-black rounded-full text-xs font-semibold px-4">Attiva Fuma+ · €{Number(plusDelivery.price_month).toFixed(2)}/mese</Button>
                  </div>
                )}
                <Row label="Servizio" value={serviceFee} />
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-white/10"><span>Totale</span><span data-testid="checkout-total" className="text-[#00E676]">€{total.toFixed(2)}</span></div>
              </div>
              <Button data-testid="place-order" disabled={!canOrder} onClick={submit} className="w-full mt-4 bg-[#00E676] text-black hover:bg-[#00c766] font-semibold rounded-full h-12 disabled:opacity-40">{loading ? "Invio…" : outOfArea ? "Fuori zona di consegna" : "Invia ordine"}</Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Row({ label, value, green, testid }) {
  return <div data-testid={testid} className="flex justify-between text-zinc-400"><span>{label}</span><span className={green ? "text-[#00E676]" : ""}>€{value.toFixed(2)}</span></div>;
}
